import React, { useEffect, useRef, useState } from 'react'

// One box per digit, the way the member app asks for a code. Behaviour worth
// having is small: advance on type, step back on backspace, take a pasted or
// autofilled code, digits only.
export default function OtpBoxes({
  value,
  length = 6,
  disabled = false,
  invalid = false,
  autoFocus = false,
  onChange,
  onComplete,
}) {
  const boxes = useRef([])
  const [focused, setFocused] = useState(-1)
  const digits = String(value || '').split('').slice(0, length)

  useEffect(() => {
    if (autoFocus) {
      const first = boxes.current[0]
      if (first) first.focus()
    }
  }, [autoFocus])

  // The error arrives while the boxes are still disabled by the in-flight
  // request, and .focus() on a disabled input does nothing — so wait for both.
  useEffect(() => {
    if (invalid && !disabled) focusAt(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invalid, disabled])

  const commit = (next) => {
    const clean = next.replace(/\D/g, '').slice(0, length)
    onChange(clean)
    if (clean.length === length && onComplete) onComplete(clean)
    return clean
  }

  const focusAt = (index) => {
    const box = boxes.current[Math.max(0, Math.min(index, length - 1))]
    if (box) box.focus()
  }

  const handleInput = (index, raw) => {
    const typed = String(raw || '').replace(/\D/g, '')
    if (!typed) return
    // A rejected code stays on screen in red. The next keystroke starts a fresh
    // one from the first box rather than editing single digits of a dead code.
    const at = invalid ? 0 : index
    const next = invalid ? [] : digits.slice()
    typed.split('').forEach((digit, offset) => {
      next[at + offset] = digit
    })
    const clean = commit(next.join(''))
    if (clean.length < length) focusAt(at + typed.length)
  }

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace') {
      event.preventDefault()
      const next = digits.slice()
      if (next[index]) {
        next[index] = ''
        commit(next.join(''))
        return
      }
      next[index - 1] = ''
      commit(next.join(''))
      focusAt(index - 1)
      return
    }
    if (event.key === 'ArrowLeft') focusAt(index - 1)
    if (event.key === 'ArrowRight') focusAt(index + 1)
  }

  const handlePaste = (index, event) => {
    event.preventDefault()
    handleInput(index, event.clipboardData.getData('text'))
  }

  return (
    <div className="d-flex justify-content-center" style={{ gap: 8 }}>
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          ref={(el) => {
            boxes.current[index] = el
          }}
          className="form-control form-control-solid text-center font-weight-bolder"
          // form-control-solid ships border and fill in the same colour, which
          // leaves six vague grey blocks and no visible keyboard focus.
          // Metronic's is-invalid draws a warning glyph inside the box, which
          // covers the digit and reads as six broken fields. A wrong code shows
          // the digits the user actually typed, in red.
          style={{
            width: 52,
            height: 60,
            fontSize: 24,
            padding: 0,
            color: invalid ? '#F64E60' : undefined,
            borderColor: invalid ? '#F64E60' : focused === index ? '#3699FF' : '#D1D3E0',
            boxShadow: focused === index && !invalid ? '0 0 0 3px rgba(54, 153, 255, 0.15)' : 'none',
            transition: 'border-color 150ms ease-out, box-shadow 150ms ease-out',
          }}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={length}
          aria-label={`หลักที่ ${index + 1}`}
          disabled={disabled}
          value={digits[index] || ''}
          onChange={(e) => handleInput(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={(e) => handlePaste(index, e)}
          onFocus={(e) => {
            setFocused(index)
            e.target.select()
          }}
          onBlur={() => setFocused((current) => (current === index ? -1 : current))}
        />
      ))}
    </div>
  )
}
